import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { shopApi } from '@wa/shared';
import type { ShopSettingsInput } from '@wa/shared';
import { Card, Field, Notice, Switch, errorText, input, primary } from './ui';

/**
 * How customers pay. Online payment runs on the client's own gateway account —
 * the keys are theirs, stored encrypted, never shown again, and PSM never holds
 * the money. The switches here are the shop-wide ceiling; each customer type
 * narrows them.
 */
export function PaymentsPanel() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['shop', 'settings'], queryFn: shopApi.settings });
  const shop = settings.data?.settings ?? null;
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [cod, setCod] = useState(false);
  const [onAccount, setOnAccount] = useState(false);
  const [online, setOnline] = useState(false);
  const [gateway, setGateway] = useState('razorpay');
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  useEffect(() => {
    if (!shop) return;
    setCod(shop.codEnabled); setOnAccount(shop.onAccountEnabled); setOnline(shop.onlineEnabled);
    setGateway(shop.gateway ?? 'razorpay'); setKeyId(shop.gatewayKeyId ?? ''); setKeySecret(''); setWebhookSecret('');
  }, [shop]);

  const save = useMutation({
    mutationFn: (patch: ShopSettingsInput) => shopApi.saveSettings(patch),
    onSuccess: () => { setError(null); setSaved(true); setTimeout(() => setSaved(false), 1500); void queryClient.invalidateQueries({ queryKey: ['shop'] }); },
    onError: (e) => setError(errorText(e, 'Could not save.')),
  });

  if (!shop) return <Notice kind="muted">{settings.isLoading ? 'Loading…' : 'Create the shop on the Overview tab first.'}</Notice>;

  return (
    <div className="flex flex-col gap-4">
      {error && <Notice kind="bad">{error}</Notice>}
      {saved && <Notice kind="good">Saved.</Notice>}
      <Card title="Ways to pay" blurb="What the shop offers at all. Each customer type can allow fewer; it cannot allow more.">
        <Switch checked={cod} onChange={setCod} label="Cash on delivery" />
        <Switch checked={onAccount} onChange={setOnAccount} label="On account" hint="Only for customers you have marked as credit-approved on their customer page." />
        <Switch checked={online} onChange={setOnline} label="Pay online" hint="Needs the gateway below. Money goes straight to your gateway account." />
      </Card>
      <Card title="Payment gateway" blurb="Your own Razorpay (or PayU) account. The key id is public; the secrets are stored encrypted and never shown again — leave them blank to keep what is saved.">
        <div className="grid gap-3 @min-[32rem]:grid-cols-2">
          <Field label="Gateway">
            <select className={input} value={gateway} onChange={(e) => setGateway(e.target.value)}>
              {(settings.data?.gateways ?? ['razorpay']).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Key id"><input className={input} value={keyId} onChange={(e) => setKeyId(e.target.value)} placeholder="rzp_live_…" autoComplete="off" /></Field>
          <Field label="Key secret" hint={shop.gatewaySecretSet ? 'A secret is saved. Type a new one to replace it.' : 'Not set yet.'}>
            <input className={input} type="password" value={keySecret} onChange={(e) => setKeySecret(e.target.value)} autoComplete="new-password" placeholder={shop.gatewaySecretSet ? '••••••••' : ''} />
          </Field>
          <Field label="Webhook secret" hint={shop.gatewayWebhookSecretSet ? 'Saved. Type a new one to replace it.' : 'From the webhook you add in the gateway dashboard.'}>
            <input className={input} type="password" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} autoComplete="new-password" placeholder={shop.gatewayWebhookSecretSet ? '••••••••' : ''} />
          </Field>
        </div>
        <p className="text-micro text-(--color-muted)">
          Webhook URL for the gateway dashboard: <code>{'{api}'}/s/{shop.slug}/payments/webhook</code> — events payment.captured and payment.failed. Online payment goes live in milestone 3; the keys can be saved now.
        </p>
        <div>
          <button type="button" className={primary} disabled={save.isPending}
                  onClick={() => save.mutate({
                    codEnabled: cod, onAccountEnabled: onAccount, onlineEnabled: online, gateway, gatewayKeyId: keyId,
                    ...(keySecret ? { gatewayKeySecret: keySecret } : {}),
                    ...(webhookSecret ? { gatewayWebhookSecret: webhookSecret } : {}),
                  })}>
            Save payments
          </button>
        </div>
      </Card>
    </div>
  );
}
